import { Box, Button, Stack, Typography } from '@mui/material'
import { Link, useNavigate } from 'react-router-dom'
import { WEBSITE_URL } from '../../../../env'
import { Dialogs, useDialogs } from '../../../../sharedComponents/src/Contexts/DialogsContext'
import BaseDialog from '../../../../sharedComponents/src/Dialogs/Common/BaseDialog'
import SignIn from '../../../../sharedComponents/src/Dialogs/Forms/SignIn'
import SignUp from '../../../../sharedComponents/src/Dialogs/Forms/SignUp'
import { analyticsManager } from '../../../../sharedComponents/src/globalHeader/services/analytics/AnalyticsManager'
import { useScreenSize } from '../../../../sharedComponents/src/hooks/useScreenSize'
import { useStoreActions } from '../../../../store/hooks'

type Variant = {
    header: { top: string; bottom?: string }
    content: { image: string; header: string; description: string }
    controls: { type: 'link' | 'action' | 'menu'; text: string; to: string; action?: any }[]
    loginHint: boolean
}

export type VariantKeys =
    | 'puzzleQuestMember'
    | 'puzzleQuestPremium'
    | 'puzzlePracticeMember'
    | 'puzzlePracticePremium'
    | 'puzzleDailyMember'

const variants: Record<VariantKeys, Variant> = {
    puzzleQuestPremium: {
        header: {
            top: 'go premium',
            bottom: 'to unlock',
        },
        content: {
            image: '/assets/images/limits-dialogs/puzzle-quest-progress.webp',
            header: '',
            description: 'To play puzzle quest you must become a premium member.',
        },
        controls: [
            { text: 'go premium', to: `${WEBSITE_URL}/memberships`, type: 'link' },
            { text: 'main menu', to: '/', type: 'menu' },
        ],
        loginHint: false,
    },
    puzzleQuestMember: {
        header: {
            top: 'unlock puzzle quest',
            bottom: 'Join for Free',
        },
        content: {
            image: '/assets/images/limits-dialogs/puzzle-quest-progress.webp',
            header: '',
            description: 'Become a member for full access!',
        },
        controls: [
            { text: 'become a member', to: Dialogs.SIGNUP, type: 'action', action: <SignUp /> },
            { text: 'main menu', to: '/', type: 'menu' },
        ],
        loginHint: true,
    },
    puzzlePracticePremium: {
        header: {
            top: 'Daily Limit Reached',
        },
        content: {
            image: '/assets/images/limits-dialogs/practice.svg',
            header: '',
            description:
                'Become a premium member to unlock full access to over 3.5 million puzzles without a daily limit',
        },
        controls: [
            { text: 'become a member', to: `${WEBSITE_URL}/memberships`, type: 'link' },
            { text: 'main menu', to: '/', type: 'menu' },
        ],
        loginHint: false,
    },
    puzzlePracticeMember: {
        header: {
            top: 'unlock puzzles',
            bottom: 'Join for Free',
        },
        content: {
            image: '/assets/images/limits-dialogs/practice.svg',
            header: 'Become a member to access puzzles and unlock countless hours of entertainment.',
            description: '',
        },
        controls: [
            { text: 'sign up', to: Dialogs.SIGNUP, type: 'action', action: <SignUp /> },
            { text: 'main menu', to: '/', type: 'menu' },
        ],
        loginHint: true,
    },
    puzzleDailyMember: {
        header: {
            top: 'Want more puzzles?',
        },
        content: {
            image: '/assets/images/limits-dialogs/practice.svg',
            header: 'Become a member for free and unlock countless hours of play.',
            description: "Signing up is faster than a knight's hop!",
        },
        controls: [
            { text: 'sign up', to: Dialogs.SIGNUP, type: 'action', action: <SignUp /> },
            { text: 'main menu', to: '/', type: 'menu' },
        ],
        loginHint: true,
    },
}

interface PuzzlesLimitDialogProps {
    variant: VariantKeys
    onClose?: () => void
}

export const PuzzlesLimitDialog: React.FC<PuzzlesLimitDialogProps> = ({ variant, onClose }) => {
    const { isMobile } = useScreenSize()
    const navigate = useNavigate()
    const { registerDialog } = useDialogs()
    const openMainMenu = useStoreActions((state) => state.setIsDrawerMenuOpen)
    const setDrawerOpen = useStoreActions((state) => state.setDrawerOpen)
    const info = variants[variant]

    return (
        <BaseDialog
            sx={{
                '.MuiDialogContent-root': {
                    width: '100%',
                    padding: '0px !important',
                    overflow: 'hidden',
                    borderRadius: 0,
                },
            }}
            name={Dialogs.PUZZLE_LIMIT}
            nonClickableBackdrop
            horizontalCentered
            fullScreen={false}
            gap={isMobile ? '28px' : '40px'}
            showClose={false}
            borderColor="#A4E9F5" // primary
        >
            {/* Title */}
            <Stack width="100%" textAlign="center" gap="20px">
                <Box>
                    <Typography textTransform="uppercase" variant={isMobile ? 'h5' : 'h3'}>
                        {info.header.top}
                    </Typography>
                    {info.header.bottom && (
                        <Typography textTransform="uppercase" variant={isMobile ? 'h5' : 'h3'} color="primary">
                            {info.header.bottom}
                        </Typography>
                    )}
                </Box>
                {(info.content.header || info.content.description) && (
                    <Box>
                        {info.content.header && (
                            <Typography variant={isMobile ? 'body2' : 'body1'}>{info.content.header}</Typography>
                        )}
                        {info.content.description && (
                            <Typography variant={isMobile ? 'body2' : 'body1'}>{info.content.description}</Typography>
                        )}
                    </Box>
                )}
            </Stack>

            <Box width="100%" display="flex" justifyContent="center">
                <img
                    src={info.content.image}
                    alt=""
                    style={{
                        width: '100%',
                        height: 'min(100%, 200px)',
                        maxHeight: isMobile ? '100px' : '160px',
                        objectFit: 'contain',
                    }}
                />
            </Box>

            {/* Buttons */}
            <Stack width="100%" gap="20px">
                {info.controls.map((control, index) =>
                    control.type === 'link' ? (
                        <Link
                            style={{ display: 'block', width: '100%' }}
                            key={control.text + control.to}
                            to={control.to}
                        >
                            <Button
                                size={isMobile ? 'small' : 'large'}
                                fullWidth
                                variant={index ? 'outlined' : 'contained'}
                            >
                                {control.text}
                            </Button>
                        </Link>
                    ) : (
                        <Button
                            key={control.text + control.to}
                            onClick={() => {
                                if (control.type === 'action') {
                                    registerDialog(control.to as Dialogs, control.action)
                                    if (control.to === Dialogs.SIGNUP) {
                                        analyticsManager.dispatchEvent('openSignup', {
                                            origin: 'puzzleLimit',
                                        })
                                    }
                                } else {
                                    openMainMenu(true)
                                    navigate(control.to)
                                    setDrawerOpen('open')
                                    onClose?.()
                                }
                            }}
                            fullWidth
                            size={isMobile ? 'small' : 'large'}
                            variant={index ? 'outlined' : 'contained'}
                        >
                            {control.text}
                        </Button>
                    ),
                )}
            </Stack>

            {info.loginHint && (
                <Typography align="center" variant={isMobile ? 'body2' : 'body1'}>
                    Already have an account?
                    <br />
                    <a
                        onClick={() => {
                            registerDialog(Dialogs.SIGNIN, <SignIn />)
                            analyticsManager.dispatchEvent('openLogin', {
                                origin: 'puzzleLimit',
                            })
                        }}
                    >
                        Log in
                    </a>
                </Typography>
            )}
        </BaseDialog>
    )
}

export default PuzzlesLimitDialog
